/**
 * HowItWorks — shared 3-step explainer used in three places:
 *   1. Landing page (`Landing.jsx` "How it works" section)
 *   2. Floating help button on the Crate (post-login home) page
 *   3. Floating help button on the Profile page
 *
 * Single source of truth for the step copy and icons. Updating the
 * Recognize/Act copy here propagates to the marketing page AND the
 * in-app help modal automatically.
 *
 * Exports:
 *   - STEPS:          the raw 3-step data array
 *   - HowItWorksGrid: the 3-card grid (no header/copy wrapper)
 *   - HowItWorksModal: Dialog wrapper that renders the grid + title
 *   - HelpButton:     floating bottom-right "?" trigger
 */
import { useState } from 'react';
import { Camera, Wand2, ShoppingCart, HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/* ============================================================
 * STEP DATA — edit copy here, both Landing + in-app modal update.
 * ============================================================ */
export const STEPS = [
  {
    num: '01',
    icon: Camera,
    accent: 'primary',
    title: 'Snap & Upload',
    copy: 'Drop a single snapshot or a whole stack at once — Spotify, Shazam, Apple Music, Instagram Reels, TikTok. Duplicates are detected before they even upload (SHA-256), so you never re-scan the same image twice.',
  },
  {
    num: '02',
    icon: Wand2,
    accent: 'lime',
    title: 'Recognize',
    copy: 'GPT-4o Vision reads the pixels. Three independent databases (iTunes + MusicBrainz + Last.fm) have to agree before a subgenre is assigned — so your Deep House crate stays Deep House and your Jazz crate stays Jazz. GetSongBPM then locks in the exact BPM and key.',
  },
  {
    num: '03',
    icon: ShoppingCart,
    accent: 'amber',
    title: 'Act',
    copy: "One-tap buy on Beatport, Traxsource, Bandcamp or iTunes. Push straight to a Spotify playlist, watch the music video, grab the iTunes ringtone, or dive into the artist's top tracks, bio, BPM and key. Mark tracks as owned and your crate becomes a tool, not a graveyard.",
  },
];

const ACCENT_MAP = {
  primary: 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10',
  lime: 'text-[var(--brand-lime)] bg-[var(--brand-lime)]/10',
  amber: 'text-[var(--brand-amber)] bg-[var(--brand-amber)]/10',
  coral: 'text-[var(--brand-coral)] bg-[var(--brand-coral)]/10',
};

/* ============================================================
 * HowItWorksGrid — 3-card grid with the Pattern A animated borders.
 * Used directly by Landing.jsx (inside its own section header) and
 * by HowItWorksModal (inside the Dialog body).
 * ============================================================ */
export function HowItWorksGrid() {
  return (
    <ol className="grid md:grid-cols-3 gap-4 list-none p-0 m-0">
      {STEPS.map((step) => {
        const Icon = step.icon;
        const accentClass = ACCENT_MAP[step.accent] || ACCENT_MAP.primary;
        return (
          <li key={step.num} className="list-none">
            <div className="crate-neon-border rounded-2xl h-full">
              <div className="bg-card rounded-[14px] p-5 h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center ${accentClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{step.num}</span>
                </div>
                <div className="font-display font-semibold text-base">{step.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.copy}</p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ============================================================
 * HowItWorksModal — Radix Dialog wrapping the grid. Controlled.
 * ============================================================ */
export function HowItWorksModal({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md sm:max-w-3xl w-[95vw] bg-card border-border max-h-[92vh] overflow-y-auto"
        data-testid="how-it-works-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-[hsl(var(--primary))]" />
            How GroovLens works
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Three taps from snapshot to crate. Here&apos;s the whole flow.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <HowItWorksGrid />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 * HelpButton — floating bottom-right "?" trigger that opens the
 * HowItWorksModal. Positioned above the mobile bottom-nav (the nav
 * sits at z-[10000] with ~64px height + iOS safe-area inset). On
 * desktop the bottom nav doesn't exist so the button sits lower.
 * ============================================================ */
export function HelpButton({ className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Plain <button> instead of shadcn Button — shadcn Button has a
          baked-in `[&_svg]:size-4` rule that forces ALL child svgs to 16px,
          which kept the ? glyph stuck small no matter what size class we
          added to the icon. Using a plain button bypasses that override
          so the icon can actually take up most of the circle. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="floating-help-button"
        aria-label="How GroovLens works"
        className={`fixed right-4 z-[9000] inline-flex items-center justify-center rounded-full w-12 h-12 shadow-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:-translate-y-1 hover:shadow-[0_0_18px_rgba(255,178,74,0.45)] hover:bg-[hsl(var(--primary))]/95 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]
          bottom-[calc(6rem+env(safe-area-inset-bottom))] md:bottom-[66px]
          ${className}`}
      >
        {/* Large ? glyph — fills most of the 48px button so it's obvious
            it's a help affordance. Roughly 2x the previous rendered size. */}
        <HelpCircle className="w-9 h-9" strokeWidth={2.25} />
      </button>
      <HowItWorksModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
